import type { PaymentInvoiceDto, SubscriptionInvoice } from '../models/billing.types'

export type PaymentFailureState = 'downgrade_pending' | 'downgraded_to_free'

export interface PaymentFailureNotice {
  state: PaymentFailureState
  downgradeAt: string | null
  portalUrl: string
  planName: string
}

type UnknownRecord = Record<string, unknown>

const ENTERPRISE_PACKAGE_IDS = new Set([4, 11, 20, 10000053, 10000065])
const PENDING_STATUSES = new Set([
  'downgrade_pending',
  'payment_failed',
  'payment_failed_pending',
  'payment_failed_pending_downgrade',
  'past_due',
  'dunning',
])
const DOWNGRADED_STATUSES = new Set([
  'downgraded_to_free',
  'payment_failed_downgraded',
  'free_due_to_payment_failure',
  'unpaid',
])

function record(value: unknown): UnknownRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {}
}

function firstValue(sources: UnknownRecord[], keys: string[]): unknown {
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key]
      if (value !== null && value !== undefined && value !== '') return value
    }
  }
  return null
}

function hasTrue(sources: UnknownRecord[], keys: string[]): boolean {
  return sources.some((source) => keys.some((key) => source[key] === true))
}

function normalizeStatus(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

export function normalizePlanName(value: unknown): string {
  const name = String(value || '').trim()
  if (!name) return ''
  if (/^free(?: plan)?$/i.test(name)) return 'Free'
  return name.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function isEnterpriseSubscription(planName: unknown, packageId: unknown): boolean {
  return /enterprise/i.test(String(planName || '')) || ENTERPRISE_PACKAGE_IDS.has(Number(packageId))
}

export function canManageSubscription(params: {
  accountUserId?: unknown
  userId?: unknown
  selectedAccountLevel?: unknown
  isSysAdmin?: boolean
}): boolean {
  const level = String(params.selectedAccountLevel || '').toLowerCase()
  return (
    String(params.accountUserId ?? '') === String(params.userId ?? '') ||
    level === 'owner' ||
    level === 'admin' ||
    Boolean(params.isSysAdmin)
  )
}

export function normalizeInvoiceHistory(
  invoices: PaymentInvoiceDto[] | null | undefined,
  currency: unknown = 'USD',
): SubscriptionInvoice[] {
  if (!Array.isArray(invoices)) return []
  const currencyCode = String(currency || 'USD').toUpperCase()

  return invoices.map((invoice, index) => {
    const total =
      invoice.total === null || invoice.total === undefined ? null : Number(invoice.total)
    const date = invoice.created_at || invoice.created || null
    const kind = invoice.kind === 'one_time' ? 'one_time' : 'subscription'
    const url = typeof invoice.url === 'string' ? invoice.url : ''
    const status = typeof invoice.status === 'string' ? invoice.status : ''
    return {
      id: url || [date, status, kind, invoice.total, index].join(':'),
      date: typeof date === 'string' ? date : null,
      dueDate: typeof invoice.dueDate === 'string' ? invoice.dueDate : null,
      amount: total !== null && Number.isFinite(total) ? total / 100 : null,
      currency: currencyCode,
      url,
      status,
      kind,
    }
  })
}

export function nextRenewalDateFromInvoices(
  invoices: Array<Pick<PaymentInvoiceDto, 'created' | 'created_at'>> | null | undefined,
): string | null {
  if (!Array.isArray(invoices)) return null
  const latest = invoices.reduce<number | null>((current, invoice) => {
    const timestamp = Date.parse(String(invoice.created_at || invoice.created || ''))
    if (!Number.isFinite(timestamp)) return current
    return current === null || timestamp > current ? timestamp : current
  }, null)
  if (latest === null) return null

  const renewal = new Date(latest)
  const renewalDay = renewal.getUTCDate()
  renewal.setUTCDate(1)
  renewal.setUTCMonth(renewal.getUTCMonth() + 1)
  const lastDay = new Date(
    Date.UTC(renewal.getUTCFullYear(), renewal.getUTCMonth() + 1, 0),
  ).getUTCDate()
  renewal.setUTCDate(Math.min(renewalDay, lastDay))
  return renewal.toISOString()
}

export function subscriptionField(accountDetail: unknown, keys: string[]): unknown {
  const detail = record(accountDetail)
  return firstValue(
    [record(detail.subscription), record(detail.billing), record(detail.billing_details), detail],
    keys,
  )
}

export function normalizePaymentFailure(accountDetail: unknown): PaymentFailureNotice | null {
  const detail = record(accountDetail)
  const lifecycle = record(detail.payment_lifecycle)
  const issue = record(detail.payment_issue || detail.payment_failure)
  const subscription = record(detail.subscription)
  const billing = record(detail.billing || detail.billing_details)
  const sources = [lifecycle, issue, detail, subscription, billing]
  const status = normalizeStatus(
    firstValue(sources, ['payment_status', 'payment_failure_status', 'state', 'stripe_status']),
  )

  let state: PaymentFailureState | null = null
  if (
    hasTrue(sources, [
      'downgraded_to_free_due_to_payment_failure',
      'is_free_due_to_payment_failure',
      'payment_failed_downgraded',
    ]) ||
    DOWNGRADED_STATUSES.has(status)
  ) {
    state = 'downgraded_to_free'
  } else if (
    hasTrue(sources, ['payment_downgrade_pending', 'downgrade_pending', 'payment_failed']) ||
    PENDING_STATUSES.has(status)
  ) {
    state = 'downgrade_pending'
  }
  if (!state) return null

  const downgradeAt = firstValue(sources, [
    'downgrade_date',
    'payment_downgrade_date',
    'scheduled_downgrade_at',
  ])
  return {
    state,
    downgradeAt: downgradeAt ? String(downgradeAt) : null,
    portalUrl: String(
      firstValue(sources, ['billing_portal_url', 'portal_url', 'manage_billing_url']) || '',
    ),
    planName: normalizePlanName(
      firstValue(sources, ['plan_name', 'previous_plan_name', 'paid_plan_name', 'package_name']),
    ),
  }
}

export function paymentFailureStorageKey(accountId: unknown): string {
  return `orphex_payment_failure_${String(accountId || 'unknown')}`
}
